// Geometry helpers shared by snapping, clamping, overlap detection and the
// drag handler. Keep this a leaf: it imports nothing from the planner beyond
// ObjectNameMap and types, which is what lets the snapping modules use it
// without importing cabinetPosition.ts back.
import * as THREE from 'three';
import {CabinetMesh} from 'components/customer/RoomPlanner/types';
import ObjectNameMap from 'components/customer/RoomPlanner/constants/ObjectNameMap';

/**
 * `toggleDoor` exists on every operation mesh; only door/drawer ops set
 * `boundsExcludeAnimatedDoorOrDrawerPart` in attachAnimations (see Preview3DCommon/animations/handler).
 *
 * @param {THREE.Object3D} node Object under the cabinet (e.g. a mesh)
 * @param {THREE.Object3D} cabinetRoot Cabinet group root; walk stops here
 * @param {boolean} includeDrawerFaces Product include_drawer_faces flag
 * @return {boolean} True if under an animated door/drawer operation root
 */
const isMeshUnderAnimatedDoorOrDrawerPart = (
    node: THREE.Object3D,
    cabinetRoot: THREE.Object3D,
    includeDrawerFaces: boolean
): boolean => {
    if (!includeDrawerFaces) return false;
    let current: THREE.Object3D | null = node;
    while (current && current !== cabinetRoot) {
        const ud = current.userData as {
            boundsExcludeAnimatedDoorOrDrawerPart?: unknown;
        };
        if (ud.boundsExcludeAnimatedDoorOrDrawerPart === true) return true;
        current = current.parent;
    }
    return false;
};

export const computeWorldBoundsExcludingAnimatedDoorParts = (
    cabinetRoot: THREE.Group<THREE.Object3DEventMap>
    /** Set true temporarily to log which meshes are skipped as door/drawer faces. */
): THREE.Box3 => {
    const values = cabinetRoot.userData?.values as
        {include_drawer_faces?: boolean} | undefined;
    const includeDrawerFaces =
        typeof values?.include_drawer_faces !== 'undefined'
            ? Boolean(values.include_drawer_faces)
            : true;

    const combinedBounds = new THREE.Box3();
    combinedBounds.makeEmpty();

    const componentBounds = new THREE.Box3();
    cabinetRoot.updateWorldMatrix(true, true);

    cabinetRoot.traverse((child) => {
        if (!child.visible || child === cabinetRoot) return;
        if (
            isMeshUnderAnimatedDoorOrDrawerPart(
                child,
                cabinetRoot,
                includeDrawerFaces
            )
        ) {
            return;
        }
        if (!(child as THREE.Mesh).isMesh) return;

        componentBounds.setFromObject(child);
        if (!componentBounds.isEmpty()) {
            combinedBounds.union(componentBounds);
        }
    });

    return combinedBounds.isEmpty()
        ? new THREE.Box3().setFromObject(cabinetRoot)
        : combinedBounds;
};

export const getGroupWidth = (group: THREE.Group<THREE.Object3DEventMap>) => {
    // Placeholders: mesh-only world bounds so overlay children (arrows,
    // measurements) don't inflate width/depth used by clamp + dashed guides.
    const bbox = group.userData?.isPlaceholder
        ? computeWorldBoundsExcludingAnimatedDoorParts(group)
        : new THREE.Box3().setFromObject(group);

    let width = bbox.max.x - bbox.min.x;
    let height = bbox.max.y - bbox.min.y;
    let depth = bbox.max.z - bbox.min.z;

    // For placeholders, if bounding box is invalid (NaN or Infinity), use fallback dimensions
    if (group.userData?.isPlaceholder) {
        const isInvalid =
            !isFinite(width) ||
            !isFinite(height) ||
            !isFinite(depth) ||
            width <= 0 ||
            height <= 0 ||
            depth <= 0;

        if (isInvalid) {
            width = 120;
            depth = 120;
            height = 200;
        }
    }

    return {width, height, depth};
};

// Wall name → cabinet rotation (rotation.z) used for wall-mounted products.
export const WALL_ROTATIONS = {
    'wall:back': 0,
    'wall:front': Math.PI,
    'wall:left': Math.PI / 2,
    'wall:right': -Math.PI / 2,
    leftWidthWall: Math.PI / 2,
    leftWidth2Wall: -Math.PI / 2,
    rightWidthWall: 0,
    rightWidth2Wall: Math.PI,
    leftDepthWall: Math.PI,
    rightDepthWall: -Math.PI / 2,
} as const;

export const getDirection = (rotation: number) => {
    const normalizedRotation =
        ((rotation % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);

    if (Math.abs(normalizedRotation) < 0.1) return 'normal';
    if (Math.abs(normalizedRotation - Math.PI / 2) < 0.1) return 'right';
    if (Math.abs(normalizedRotation - Math.PI) < 0.1) return 'back';
    if (Math.abs(normalizedRotation - (3 * Math.PI) / 2) < 0.1) return 'left';
    return 'normal';
};

export type Direction = ReturnType<typeof getDirection>;

// An appliance GLB placeholder rather than a cabinet.
export const isPlaceholderProduct = (group: THREE.Object3D): boolean =>
    Boolean(group.userData?.isPlaceholder);

/**
 * Yaw that holds an item's facing. Placeholders store it on `rotation.y`;
 * cabinets use `rotation.z`. Anything that may be handed either, such as a
 * product's snapping facts or a drag's axes, reads this instead of
 * `rotation.z`.
 * @param {THREE.Object3D} group Cabinet or placeholder
 * @return {number} Facing rotation in radians
 */
export const getFacingRotation = (group: THREE.Object3D): number =>
    isPlaceholderProduct(group) ? group.rotation.y : group.rotation.z;

// Whether a product faces the front or back wall, so its width runs along x.
export const facesFrontOrBack = (direction: Direction) =>
    direction === 'normal' || direction === 'back';

export const applyBulkheadOffset = (
    x: number,
    z: number,
    cabinet: CabinetMesh,
    direction?: 'normal' | 'right' | 'back' | 'left'
) => {
    const values = cabinet.userData.values;

    const carcassThickness = Number(values?.cabinet_carc_thickness) || 0;
    const materialThickness = Number(values?.cabinet_ext_thickness) || 0;

    const totalThickness = (carcassThickness + materialThickness) * 0.2;
    const halfTotalThickness = totalThickness / 2;

    const flip = direction === 'left' || direction === 'right' ? -1 : 1;

    return {
        x: x + flip * halfTotalThickness,
        z: z - flip * halfTotalThickness,
        thicknessOffset: totalThickness,
    };
};

export const isAdjustableLeg = (object: THREE.Object3D) =>
    (object as THREE.Mesh).isMesh &&
    object.name === ObjectNameMap.adjustableLeg;

/**
 * How far forward a cabinet's legs reach along a direction: the largest
 * projection of any leg's box onto it. Legs are recognised by mesh name
 * (isAdjustableLeg) and measured with the cabinet's other facts
 * (ProductFacts.legBBoxes, snapping/context.ts).
 * @param {THREE.Box3[]} legBBoxes The cabinet's leg boxes.
 * @param {THREE.Vector3} direction A quarter-turn unit vector in the floor
 * plane, normally the cabinet's front normal.
 * @return {number|null} The front-most leg face, or null without legs.
 */
export const legFrontDepth = (
    legBBoxes: readonly THREE.Box3[],
    direction: THREE.Vector3
) => {
    const fronts = legBBoxes.map((bbox) =>
        Math.max(bbox.min.dot(direction), bbox.max.dot(direction))
    );

    return fronts.length ? Math.max(...fronts) : null;
};

// One face of an axis-aligned box: its outward normal, and its position along
// that normal's axis.
export interface CabinetFace {
    normal: THREE.Vector3;
    position: number;
}

/**
 * Calculates the distance vector between two axis-aligned bounding boxes (AABBs).
 * Each component of the returned Vector3 represents the separation along that axis.
 * If the boxes overlap along an axis, the corresponding component will be 0.
 *
 * @param {THREE.Box3} bboxA - The first bounding box.
 * @param {THREE.Box3} bboxB - The second bounding box.
 * @return {THREE.Vector3} A vector representing the distance along x, y, and z axes between the boxes.
 */
export function getBBoxDistance(
    bboxA: THREE.Box3,
    bboxB: THREE.Box3
): THREE.Vector3 {
    const dx = Math.max(
        0,
        bboxA.min.x - bboxB.max.x,
        bboxB.min.x - bboxA.max.x
    );
    const dy = Math.max(
        0,
        bboxA.min.y - bboxB.max.y,
        bboxB.min.y - bboxA.max.y
    );
    const dz = Math.max(
        0,
        bboxA.min.z - bboxB.max.z,
        bboxB.min.z - bboxA.max.z
    );
    return new THREE.Vector3(dx, dy, dz);
}

// A horizontal axis of the room plan. Read through the accessors below, so
// one function serves both axes.
export type PlanAxis = 'x' | 'z';

export const along = (vector: THREE.Vector3, axis: PlanAxis) =>
    axis === 'x' ? vector.x : vector.z;
export const minAlong = (bbox: THREE.Box3, axis: PlanAxis) =>
    axis === 'x' ? bbox.min.x : bbox.min.z;
export const maxAlong = (bbox: THREE.Box3, axis: PlanAxis) =>
    axis === 'x' ? bbox.max.x : bbox.max.z;

/**
 * Where the drag target goes along an axis so the dragged box lines up with
 * the other's min edge or its max edge, whichever the box is nearer to.
 * @param {PlanAxis} axis The axis to align along.
 * @param {THREE.Box3} shiftedBBox The dragged box at the drag target.
 * @param {THREE.Box3} otherBBox The box it aligns to.
 * @param {THREE.Vector3} targetPos The drag target.
 * @return {number} The aligned target along the axis.
 */
export const alignToNearerEdge = (
    axis: PlanAxis,
    shiftedBBox: THREE.Box3,
    otherBBox: THREE.Box3,
    targetPos: THREE.Vector3
): number => {
    const toMin = Math.abs(
        minAlong(shiftedBBox, axis) - minAlong(otherBBox, axis)
    );
    const toMax = Math.abs(
        maxAlong(shiftedBBox, axis) - maxAlong(otherBBox, axis)
    );

    return toMin <= toMax
        ? along(targetPos, axis) +
              (minAlong(otherBBox, axis) - minAlong(shiftedBBox, axis))
        : along(targetPos, axis) +
              (maxAlong(otherBBox, axis) - maxAlong(shiftedBBox, axis));
};
